export const normalizeText = value => String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const containsSearch = (item, fields, search) => {
    const normalizedSearch = normalizeText(search);
    if (!normalizedSearch) return true;

    return fields.some(field => {
        const value = typeof field === "function" ? field(item) : item?.[field];
        return normalizeText(value).includes(normalizedSearch);
    });
};

const normalizeSource = value => normalizeText(value).replace(/_/g, "");
const normalizeId = value => {
    if (value === null || value === undefined || value === "" || value === -1) return null;
    return String(value);
};

const hasSupport = garrison => Number(garrison?.amountOfSupport ?? 0) > 0;

export const getGarrisonDispatchType = garrison => {
    if (garrison?.garrisonType) return garrison.garrisonType;
    return garrison?.garrisonStatus ? "available" : "dispatch";
};

export const getGarrisonType = garrison => {
    if (garrison?.isPanic) return "panic";
    return getGarrisonDispatchType(garrison);
};

export const getAttendanceType = attendance => {
    if (attendance?.isPanic) return "panic";
    return attendance?.attendanceType ?? "onHold";
};

export const getTaskType = task => task?.taskType ?? "onHold";

export const hasVehicle = garrison => {
    return garrison?.garrisonVehicleType !== "FOOT";
};

const getGarrisonDispatchGroupId = garrison => {
    const garrisonType = getGarrisonType(garrison);
    if (garrisonType === "dispatchSupport") return normalizeId(garrison?.supportForGarrisonDispatchId);
    if (garrisonType === "dispatch") return normalizeId(garrison?.dispatchId);
    return null;
};

const buildMatchingGarrisonGroups = (garrisons, matcher) => garrisons.reduce((groups, garrison) => {
    if (!matcher(garrison)) return groups;

    const groupId = getGarrisonDispatchGroupId(garrison);
    if (groupId) groups.add(groupId);

    return groups;
}, new Set());

const matchesGarrisonGroupFilter = (garrison, matcher, matchingGroups) => {
    if (matcher(garrison)) return true;

    const groupId = getGarrisonDispatchGroupId(garrison);
    return Boolean(groupId && matchingGroups.has(groupId));
};

const matchesAttendanceSource = (attendance, source) => {
    if (!source) return true;
    if (source === "dispatchHelp") {
        return Boolean(attendance?.hasChat)
            || attendance?.sourceName === "dispatchHelp"
            || attendance?.sourceName === "dispatchHelpNotRead";
    }
    if (source === "dispatchHelpNotRead") return Boolean(attendance?.hasUnreadChat);

    return attendance?.sourceName === source;
};

const taskSourceMap = {
    attendance: ["ATTENDANCE", "attendance"],
    sos: ["SOS_SCHEDULED_VISIT", "sos"],
    janitorial: ["JANITORIAL", "janitorial"],
};

const matchesTaskSource = (task, source) => {
    if (!source) return true;

    const acceptedSources = taskSourceMap[source] ?? [source];
    return acceptedSources.some(item => normalizeSource(item) === normalizeSource(task?.source));
};

const matchesGarrisonStatus = (garrison, status) => {
    if (!status) return true;

    const garrisonType = getGarrisonType(garrison);
    if (status === "dispatch") return garrisonType === "dispatch" || garrisonType === "dispatchSupport";
    if (status === "dispatchSupport") return garrisonType === "dispatchSupport" || (garrisonType === "dispatch" && hasSupport(garrison));

    return garrisonType === status;
};

const matchesGarrisonText = (garrison, search) => containsSearch(garrison, [
    "garrisonName",
    "garrisonTeam",
    "garrisonVehicleClazz",
    "garrisonVehiclePlate",
    "place",
    "garrisonIntendedFunction",
    "contingentDisplay",
    "dispatchAddress",
    "dispatchNature",
    "occurrenceNumber",
    "checkbookNumber",
    "garrisonSectors",
    "leaderNamewar",
    "vehiclesPrefix",
    item => Array.isArray(item?.garrisonIntegrants) ? item.garrisonIntegrants.join(", ") : item?.garrisonIntegrants,
], search);

const buildGarrisonTextGroups = (garrisons, search) => {
    if (!normalizeText(search)) return new Set();

    return garrisons.reduce((groups, garrison) => {
        if (!matchesGarrisonText(garrison, search)) return groups;

        const garrisonType = getGarrisonType(garrison);
        const groupId = getGarrisonDispatchGroupId(garrison);

        if (groupId && (garrisonType === "dispatchSupport" || (garrisonType === "dispatch" && hasSupport(garrison)))) {
            groups.add(groupId);
        }

        return groups;
    }, new Set());
};

const matchesGarrisonSearchWithGroup = (garrison, search, matchingTextGroups) => {
    if (!normalizeText(search)) return true;
    if (matchesGarrisonText(garrison, search)) return true;

    const groupId = getGarrisonDispatchGroupId(garrison);
    return Boolean(groupId && matchingTextGroups.has(groupId));
};

export const filterAttendances = (attendances, filters) => attendances.filter(attendance => {
    if (filters.status && attendance.attendanceType !== filters.status) return false;
    if (!matchesAttendanceSource(attendance, filters.source)) return false;

    return containsSearch(attendance, [
        "protocol",
        "natureText",
        "place",
        "sectors",
        "priority",
        "vehiclePlate",
        "garrisonIdentification",
        "ramalIdentify",
        "vehicleType",
    ], filters.search);
});

export const filterTasks = (tasks, filters) => tasks.filter(task => {
    if (filters.status && task.taskType !== filters.status) return false;
    if (!matchesTaskSource(task, filters.source)) return false;

    return containsSearch(task, [
        "reasonText",
        "sourceText",
        "placeText",
    ], filters.search);
});

export const filterGarrisons = (garrisons, filters) => {
    const sourceMatcher = garrison => !filters.source || garrison?.dispatchSource === filters.source;
    const intendedFunctionMatcher = garrison => !filters.intendedFunction || normalizeText(garrison?.garrisonIntendedFunction) === normalizeText(filters.intendedFunction);
    const compositionMatcher = garrison => {
        if (filters.composition === "WITH_VEHICLE") return hasVehicle(garrison);
        if (filters.composition === "WITHOUT_VEHICLE") return !hasVehicle(garrison);
        return true;
    };
    const matchingSourceGroups = buildMatchingGarrisonGroups(garrisons, sourceMatcher);
    const matchingIntendedFunctionGroups = buildMatchingGarrisonGroups(garrisons, intendedFunctionMatcher);
    const matchingCompositionGroups = buildMatchingGarrisonGroups(garrisons, compositionMatcher);
    const matchingTextGroups = buildGarrisonTextGroups(garrisons, filters.search);

    return garrisons.filter(garrison => {
        if (!matchesGarrisonStatus(garrison, filters.status)) return false;
        if (!matchesGarrisonGroupFilter(garrison, sourceMatcher, matchingSourceGroups)) return false;
        if (!matchesGarrisonGroupFilter(garrison, intendedFunctionMatcher, matchingIntendedFunctionGroups)) return false;
        if (!matchesGarrisonGroupFilter(garrison, compositionMatcher, matchingCompositionGroups)) return false;

        return matchesGarrisonSearchWithGroup(garrison, filters.search, matchingTextGroups);
    });
};

export const getGarrisonDerivedCounts = garrisons => garrisons.reduce((counts, garrison) => {
    const source = garrison.dispatchSource;
    const composition = hasVehicle(garrison) ? "WITH_VEHICLE" : "WITHOUT_VEHICLE";
    const nextCounts = {
        ...counts,
        [composition]: (counts[composition] ?? 0) + 1,
    };

    if (!source) return nextCounts;

    return {
        ...nextCounts,
        [source]: (counts[source] ?? 0) + 1,
    };
}, {});
